import React from "react";

import { Formik } from "formik";
import { Box } from "@mui/material";
import * as Yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import ModernModal from "../../../common/ModernModal";
import {
  ModernFormField,
  ModernSubmitButton,
  ModernFormAlert,
} from "../../../common/ModernForm";
import { projectsService } from "../../services";
import { CreateProjectPayload } from "../../services/projects.service";

interface Props {
  handleClose: () => void;
  open: boolean;
}

export default function CreateProjectDialog({ handleClose, open }: Props) {
  const queryClient = useQueryClient();

  const { mutateAsync, isError, error, isPending, isSuccess } = useMutation({
    mutationKey: ["create-project"],
    mutationFn: (values: CreateProjectPayload) =>
      projectsService.createProject(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setTimeout(handleClose, 1500);
    },
  });

  const handleCreateProject = async (values: CreateProjectPayload) => {
    await mutateAsync(values);
  };

  return (
    <ModernModal
      open={open}
      onClose={handleClose}
      title="Create New Project"
      maxWidth="sm"
    >
      <Formik
        initialValues={{ project_name: "", project_description: "" }}
        validationSchema={Yup.object({
          project_name: Yup.string()
            .min(2, "Project name must be at least 2 characters")
            .max(50, "Project name must be less than 50 characters")
            .required("Project name is required"),
          project_description: Yup.string().max(
            200,
            "Description must be less than 200 characters",
          ),
        })}
        onSubmit={handleCreateProject}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {isError && (
                <ModernFormAlert severity="error">
                  {error?.message ||
                    "Failed to create project. Please try again."}
                </ModernFormAlert>
              )}

              {isSuccess && (
                <ModernFormAlert severity="success">
                  Project Created Successfully!
                </ModernFormAlert>
              )}

              <ModernFormField
                label="Project Name"
                name="project_name"
                error={
                  !!formik.errors.project_name && formik.touched.project_name
                }
                helperText={
                  formik.touched.project_name ? formik.errors.project_name : ""
                }
                value={formik.values.project_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter a descriptive project name"
                required
              />

              <ModernFormField
                label="Project Description"
                name="project_description"
                multiline
                rows={3}
                error={
                  !!formik.errors.project_description &&
                  formik.touched.project_description
                }
                helperText={
                  formik.touched.project_description
                    ? formik.errors.project_description
                    : "Optional: Describe what this project is about"
                }
                value={formik.values.project_description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Briefly describe your project goals and content focus"
              />

              <ModernSubmitButton
                label="Create Project"
                loading={isPending}
                disabled={
                  isPending || isSuccess || !formik.isValid || !formik.dirty
                }
                fullWidth
              />
            </Box>
          </form>
        )}
      </Formik>
    </ModernModal>
  );
}
